import React, { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState({
    category: null,
    question: null,
  });

  const taxDocuments = [
    {
      form: "W-2",
      color: "bg-[#FF4D6D]",
      description: "Jobs that withhold taxes from earned income.",
    },
    {
      form: "1099 NEC\n1099 MIS",
      color: "bg-[#4ECCA3]",
      description:
        "You were paid the full amount without tax retentions for jobs that do not withhold taxes from the earned income. (E.g.: Uber, Doordash, Lyft).",
    },
    {
      form: "1095 A",
      color: "bg-[#FFA726]",
      description:
        "Health Insurance through the Health Marketplace (E.g.: Obama Care, CuidadoSalud.gov).",
    },
    {
      form: "1098 T",
      color: "bg-[#00838F]",
      description:
        "If you studied at accredited institutions (E.g.: College and/or University).",
    },
    {
      form: "1098 E",
      color: "bg-[#7986CB]",
      description: "Interest generated by student loans.",
    },
    {
      form: "1098",
      color: "bg-[#00BCD4]",
      description:
        "Homeowner. This form resumes information related to your mortgage (E.g.: Insterest, Principal Payments, Property Taxes, etc.).",
    },
    {
      form: "W-10",
      color: "bg-[#26A69A]",
      description:
        "If you paid for the care of dependents under 13 years of age. (E.g.: Daycare, Tutoring, etc.).",
    },
    {
      form: "1099",
      color: "bg-[#FF8A65]",
      description:
        "Stocks. The 1099 Statement is the annual report of your stocks (E.g.: Bitcoin, Robinhood, etc.).",
    },
  ];

  const faqCategories = {
    personal: {
      title: "Personal Taxes",
      questions: [
        {
          question: "When is the tax filing deadline?",
          answer:
            "For most individuals, the federal tax filing deadline is April 15. If this date falls on a weekend or holiday, the deadline is typically extended to the next business day.",
        },
        {
          question: "How can I check the status of my tax refund?",
          answer:
            "You can check your refund status using the IRS's \"Where's My Refund?\" tool on their website. You'll need your Social Security number, filing status, and the exact refund amount.",
        },
        {
          question: "What deductions and credits am I eligible for?",
          answer:
            "Eligibility for deductions and credits depends on various factors, including income, filing status, and specific expenses. Common deductions include mortgage interest and charitable contributions, while credits may include the Earned Income Tax Credit and Child Tax Credit.",
        },
        {
          question:
            "How do I file an extension for personal and/or business income taxes?",
          answer:
            "We can file the IRS Form 4868 for you and your business by the original tax deadline. This grants you an additional six months to file your return, moving the deadline to October 15. However, any taxes owed are still due by the original deadline to avoid penalties.",
        },
        {
          question: "What should I do if I can't pay my tax bill?",
          answer:
            "If you're unable to pay your tax bill in full, the IRS offers payment plans and installment agreements. It's important to contact the IRS promptly to discuss your options and avoid additional penalties and interest.",
        },
      ],
    },
    business: {
      title: "Business Taxes",
      questions: [
        {
          question: "What are the different types of business taxes?",
          answer:
            "Businesses may be subject to various taxes, including income tax, self-employment tax, employment taxes, and excise taxes. The specific taxes applicable depend on the business structure and activities.",
        },
        {
          question: "How do I determine my business's tax obligations?",
          answer:
            "Your tax obligations are influenced by your business structure (e.g., sole proprietorship, partnership, corporation) and the nature of your operations. We can help clarify your specific responsibilities.",
        },
        {
          question: "What expenses can I deduct as a business owner?",
          answer:
            "Deductible business expenses must be both ordinary (common in your industry) and necessary (helpful and appropriate for your business). This can include costs like rent, utilities, office supplies, and certain travel expenses.",
        },
        {
          question: "When are estimated tax payments due for businesses?",
          answer:
            "For estimated tax purposes, the year is divided into four payment periods, each with a specific due date: April 15, June 15, September 15, and January 15 of the following year. Timely payments are crucial to avoid penalties.",
        },
      ],
    },
  };

  const toggleQuestion = (category, index) => {
    setOpenIndex((prev) => {
      if (prev.category === category && prev.question === index) {
        return { category: null, question: null };
      }
      return { category, question: index };
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-24">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-4xl font-bold text-center text-gray-900 mb-12">
          Frequently Asked Questions
        </h1>

        {/* Tax Documents Checklist Section */}
        <div className="mb-16">
          <div className="bg-[#B71C1C] text-white py-4 px-6 text-2xl font-bold text-center rounded-t-lg">
            TAX DOCUMENTS CHECKLIST
          </div>
          <div className="bg-white p-6 rounded-b-lg shadow-md">
            <p className="text-gray-700 text-center mb-8">
              Documents required and/or necessary to file your Personal Income
              Tax Return. You must present those that apply to your case.
            </p>
            <div className="space-y-3">
              {taxDocuments.map((doc, index) => (
                <div key={index} className="flex items-start">
                  <div
                    className={`${doc.color} text-white px-4 py-2 rounded-lg min-w-[120px] text-center font-semibold whitespace-pre-line`}
                  >
                    {doc.form}
                  </div>
                  <div className="flex-1 ml-4 bg-gray-100 p-3 rounded-lg">
                    {doc.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-8">
          {Object.entries(faqCategories).map(([categoryKey, category]) => (
            <div key={categoryKey} className="space-y-4">
              <h2 className="text-2xl font-semibold text-gray-800 mb-4">
                {category.title}
              </h2>

              {category.questions.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
                >
                  <button
                    className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none hover:bg-gray-50"
                    onClick={() => toggleQuestion(categoryKey, index)}
                  >
                    <span className="text-lg font-medium text-gray-900">
                      {faq.question}
                    </span>
                    <span className="text-red-400 ml-4 flex-shrink-0">
                      {openIndex.category === categoryKey &&
                      openIndex.question === index ? (
                        <ChevronUp size={24} />
                      ) : (
                        <ChevronDown size={24} />
                      )}
                    </span>
                  </button>

                  {openIndex.category === categoryKey &&
                    openIndex.question === index && (
                      <div className="px-6 pb-4 text-gray-600 bg-gray-50">
                        {faq.answer}
                      </div>
                    )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FAQ;
